import { request } from "../../utils/request";

/** GET /points/wallet/{userId} 成功体 */
export interface PointsWalletData {
  userId: number;
  availablePoint: number;
  totalRechargePoint: number;
  totalConsumePoint: number;
  frozenPoint: number;
  accountStatus: string;
}

export interface PointsWalletResponse {
  code: number;
  data: PointsWalletData;
  msg: string;
}

/** 同一 userId 并发只打一条 HTTP，避免登录瞬间多组件同时拉钱包 */
const pointsWalletInflight = new Map<string, Promise<PointsWalletResponse>>();

/** 当前用户积分钱包（可用积分等） */
export const getPointsWalletApi = (userId: number | string): Promise<PointsWalletResponse> => {
  const key = encodeURIComponent(String(userId));
  const existing = pointsWalletInflight.get(key);
  if (existing) return existing;
  const p = request<PointsWalletResponse>(`/points/wallet/${key}`, {
    method: "GET",
  }).finally(() => {
    pointsWalletInflight.delete(key);
  });
  pointsWalletInflight.set(key, p);
  return p;
};

/** 通知顶栏重新拉取 /points/wallet/{userId}，页面切换不影响布局层更新 */
export const triggerPointsRefresh = (): void => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event("points:refresh"));
};
/** 顶栏等：千分位展示 */
export function formatAvailablePointsLabel(value: number): string {
  const n = Math.trunc(Number.isFinite(value) ? value : 0);
  return n.toLocaleString("zh-CN");
}

/** 兼容旧字段名；内部走 /points/wallet/{userId} */
export interface PointsSummaryResponse {
  code: number;
  data: {
    totalPoints: number;
    availablePoints: number;
    usedPoints: number;
    lastCalcTime: string;
  };
  msg: string;
}

const emptySummary = (): PointsSummaryResponse => ({
  code: -1,
  data: {
    totalPoints: 0,
    availablePoints: 0,
    usedPoints: 0,
    lastCalcTime: "",
  },
  msg: "",
});

/** 获取积分汇总信息（头部导航栏等）：映射自钱包接口 `availablePoint` */
export const getPointsSummaryApi = async (): Promise<PointsSummaryResponse> => {
  try {
    const { globalStore } = await import("../../utils/globalStore");
    const user = globalStore.getUserInfo() as { id?: number } | null | undefined;
    const uid = user?.id;
    if (uid == null || uid === 0) {
      return emptySummary();
    }
    const res = await getPointsWalletApi(uid);
    if (res.code !== 0 || !res.data) {
      return {
        code: res.code,
        data: {
          totalPoints: 0,
          availablePoints: 0,
          usedPoints: 0,
          lastCalcTime: "",
        },
        msg: res.msg || "",
      };
    }
    const d = res.data;
    return {
      code: 0,
      data: {
        totalPoints: d.availablePoint + d.frozenPoint,
        availablePoints: d.availablePoint,
        usedPoints: d.totalConsumePoint,
        lastCalcTime: "",
      },
      msg: res.msg || "",
    };
  } catch {
    return emptySummary();
  }
};

/** GET /points/operation-log/personal/income 列表项 */
export interface PersonalIncomeLogItem {
  /** 若后端返回唯一主键则优先用于列表 key */
  id?: string | number;
  pointsIncome: number;
  pointType: string;
  source: string;
  createTime: string;
  expireTime: string;
  status: string;
}

export interface PersonalIncomeLogResponse {
  code: number;
  data: {
    list: PersonalIncomeLogItem[];
    total: number;
  };
  msg?: string;
}

const compactQuery = <T extends Record<string, unknown>>(obj: T): T => {
  const out = { ...obj };
  for (const k of Object.keys(out)) {
    if (out[k] === undefined || out[k] === null || out[k] === "") {
      delete (out as Record<string, unknown>)[k];
    }
  }
  return out;
};

/**
 * 分页：个人积分获取记录
 * GET /points/operation-log/personal/income
 */
export const getPointsIncomeRecordsApi = (params: {
  pageNo: number;
  pageSize: number;
  userId: number;
  status?: string;
  timeOrder?: "asc" | "desc";
  /** 取消未完成的请求，避免旧响应当作当前页数据写入 */
  signal?: AbortSignal;
}) => {
  const { signal, ...q } = params;
  return request<PersonalIncomeLogResponse>(`/points/operation-log/personal/income`, {
    method: "GET",
    params: compactQuery({
      pageNo: q.pageNo,
      pageSize: q.pageSize,
      userId: q.userId,
      status: q.status,
      timeOrder: q.timeOrder,
    }),
    signal,
  });
};

/** GET /points/operation-log/personal/expenditure 列表项 */
export interface PersonalExpenditureLogItem {
  /** 消耗积分数（后端示例为负，如 -6） */
  expenditurePoints: number;
  /** 功能类型，如 API翻译 */
  functionType: string;
  /** 任务名称，如 字幕翻译任务_B */
  taskName: string;
  /** 视频时长（秒） */
  duration: number;
  /** 提交时间 */
  createTime: string;
  /** 流水 ID（每次调用唯一），如 FE202603230002 */
  serialId: string;
  /** 状态，如 已扣除 */
  status: string;
  /** 积分流水号，如 PT-20260731-11D03824B */
  recordNo?: string;
  /** 业务任务 ID，如 2047310182 */
  businessTaskId?: string;
  /** 任务计费单号，如 PB-20260731-0001 */
  billingNo?: string;
  /** 扣减来源枚举：member（会员积分） / permanent（积分包） / mixed（混合扣减），新接口可能置空 */
  pointSource?: string;
  /** 扣减来源展示名（后端下发中文，如「积分钱包」）；表格「抵扣来源」列直接渲染此字段 */
  deductionSource?: string;
  /** 会员积分扣减数（mixed 时使用） */
  memberPoint?: number;
  /** 积分包扣减数（mixed 时使用） */
  permanentPoint?: number;
  /** 明细扣减分摊 */
  allocations?: PointAllocationDetailRespVO[];
  /** 任务失败或退款原因 */
  failReason?: string;
}

/** 积分消耗 / 获取时的分摊明细 */
export interface PointAllocationDetailRespVO {
  /** 扣减来源：member / permanent / gift 等 */
  source?: string;
  /** 该来源扣减积分数 */
  points?: number;
}

export interface PersonalExpenditureLogResponse {
  code: number;
  data: {
    list: PersonalExpenditureLogItem[];
    total: number;
  };
  msg?: string;
}

/**
 * 分页：个人积分消耗记录
 * GET /points/operation-log/personal/expenditure
 */
export const getPointsExpenditureRecordsApi = (params: {
  pageNo: number;
  pageSize: number;
  userId: number;
  status?: string;
  timeOrder?: "asc" | "desc";
  signal?: AbortSignal;
}) => {
  const { signal, ...q } = params;
  return request<PersonalExpenditureLogResponse>(`/points/operation-log/personal/expenditure`, {
    method: "GET",
    params: compactQuery({
      pageNo: q.pageNo,
      pageSize: q.pageSize,
      userId: q.userId,
      status: q.status,
      timeOrder: q.timeOrder,
    }),
    signal,
  });
};

export interface PointsStatusOption {
  value: string;
  label: string;
}

/**
 * 积分状态筛选项
 * GET /points/operation-log/status-options?type=1|2
 * 1 个人积分获取 2 个人积分消耗
 */
export const getPointsOperationLogStatusOptionsApi = (type: 1 | 2) =>
  request<{ code: number; data: PointsStatusOption[]; msg?: string }>(`/points/operation-log/status-options`, {
    method: "GET",
    params: { type },
  });

/**
 * API 源文档当前版本（后端返回的元信息结构）
 * GET /points/api-source-doc/current
 * - data.content 是字符串，由 Markdown 解析出的前端渲染 JSON
 * - 需在调用方 JSON.parse 后得到 ApiDocData
 */
export interface ApiSourceDocRaw {
  id: number;
  versionNo: number;
  fileName: string;
  filePath: string;
  fileUrl: string;
  fileSize: number;
  contentSha256: string;
  /** 由 Markdown 解析出的前端渲染 JSON（字符串） */
  content: string;
  tagCount: number;
  createdBy: number;
  createdByName: string;
}

/**
 * API 源文档当前版本
 * GET /points/api-source-doc/current
 * 返回文档元信息，真正的前端渲染 JSON 在 data.content 字段（字符串，需 JSON.parse）
 */
export const getApiSourceDocApi = () =>
  request<{
    code: number;
    data: ApiSourceDocRaw;
    msg?: string;
  }>(`/points/api-source-doc/current`, {
    method: "GET",
    // 接口文档为公开页面，未登录也可查看：401 时不自动跳转登录页
    skipAuthRedirect: true,
  });
